<template>
  <div class="rupturas-dashboard">
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        bottom
    >
      <div class="d-flex align-center">
        <v-icon left :color="snackbar.iconColor">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false" :color="snackbar.iconColor">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>

    <PainelFiltros
        titulo="Filtros de Rupturas"
        :loading="carregando"
        :opcoes-filiais="filiaisFiltro"
        :filiais-iniciais="filiaisIniciais"
        :minha-filial="usuario.idfilial"
        :atalhos-filiais-ativos="['todas', 'minha', 'lojas', 'depositos']"
        storage-key="rupturas_filtros"
        @filtrar="handleFiltrar"
        @limpar="handleLimpar"
    />

    <div v-if="carregando && !temDados" class="loading-state">
      <v-progress-circular indeterminate color="#023449" size="64"></v-progress-circular>
      <p class="mt-4 loading-text">Carregando rupturas...</p>
    </div>

    <div v-else-if="!carregando && !temDados" class="no-data-message">
      <v-icon size="64" color="#cbd5e1">mdi-inbox</v-icon>
      <h3>Nenhuma ruptura encontrada</h3>
      <p>Ajuste os filtros ou clique em "Buscar" para visualizar dados</p>
    </div>

    <template v-else>
      <RupturasResumo :resumo="resumo" :periodo="periodo" :cache="cache" />

      <RupturasHierarquia
          :produtos="produtos"
          :filiais="dadosFiliais"
          :categorias="dadosCategorias"
          :fornecedores="dadosFornecedores"
          :loading="carregando"
      />
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import mixinFilial from '../../util/mixinFilial';
import PainelFiltros from '../UI/Filtros/PainelFiltros.vue';
import RupturasResumo from './RupturasResumo.vue';
import RupturasHierarquia from './RupturasHierarquia.vue';
import axios from 'axios';

export default {
  name: 'RupturasDashboard',

  mixins: [mixinFilial],

  components: {
    PainelFiltros,
    RupturasResumo,
    RupturasHierarquia
  },

  data() {
    return {
      carregando: false,
      filiaisIniciais: [],
      resumo: {},
      periodo: {},
      cache: {},
      produtos: [],
      dadosFiliais: [],
      dadosCategorias: [],
      dadosFornecedores: [],
      snackbar: {
        show: false,
        message: '',
        color: 'error',
        icon: 'mdi-alert-circle',
        iconColor: 'white',
        timeout: 6000
      }
    };
  },

  computed: {
    ...mapState(['backendUrl', 'usuario']),

    temDados() {
      return this.produtos.length > 0;
    },

    filiaisFiltro() {
      if (this.filiais.lista) {
        return this.filiais.lista.filter(v =>
            ![4, 6, 12, 13, 25, 28, 29, 33, 37, 39, 40, 42, 43, 44].includes(v.idfilial)
        );
      }
      return [];
    }
  },

  mounted() {
    this.filiaisIniciais = [this.usuario.idfilial];
  },

  methods: {
    showSnackbar(message, type = 'error') {
      const config = {
        error:   { color: 'error',   icon: 'mdi-alert-circle',  iconColor: 'white' },
        success: { color: 'success', icon: 'mdi-check-circle',  iconColor: 'white' },
        warning: { color: 'warning', icon: 'mdi-alert',         iconColor: 'white' },
        info:    { color: 'info',    icon: 'mdi-information',   iconColor: 'white' }
      };
      const cfg = config[type] || config.error;
      this.snackbar = { show: true, message, ...cfg, timeout: type === 'error' ? 8000 : 6000 };
    },

    validarFiltros(filtros) {
      if (!filtros.dataInicio || !filtros.dataFim) {
        this.showSnackbar('Selecione um período para continuar.', 'warning');
        return false;
      }
      if (!filtros.filiais || filtros.filiais.length === 0) {
        this.showSnackbar('Selecione ao menos uma filial para continuar.', 'warning');
        return false;
      }
      return true;
    },

    handleFiltrar(filtros) {
      if (!this.validarFiltros(filtros)) return;
      this.carregarDados(filtros);
    },

    handleLimpar() {
      this.resetDados();
    },

    montarPayload(filtros) {
      const payload = {
        acao: 'listar',
        data_inicio: filtros.dataInicio,
        data_fim: filtros.dataFim
      };

      const filiais = (filtros.filiais || []).map(Number).filter(Boolean);
      if (filiais.length > 0) {
        payload.filiais = filiais.join(',');
      }

      return payload;
    },

    async carregarDados(filtros) {
      try {
        this.carregando = true;

        const payload = this.montarPayload(filtros);
        const response = await axios.post(`${this.backendUrl}rupturas-abastecimento`, payload);

        if (response.data && response.data.erro) {
          this.showSnackbar(`Erro: ${response.data.erro}`, 'error');
          this.resetDados();
          return;
        }

        if (response.data) {
          this.resumo = response.data.resumo || {};
          this.periodo = response.data.periodo || {};
          this.cache = response.data.cache || {};
          this.produtos = response.data.produtos || [];
          this.dadosFiliais = response.data.filiais || [];
          this.dadosCategorias = response.data.categorias || [];
          this.dadosFornecedores = response.data.fornecedores || [];
        } else {
          this.resetDados();
        }

      } catch (error) {
        console.error('Erro ao carregar rupturas:', error);
        this.showSnackbar('Erro ao carregar dados. Tente novamente.', 'error');
        this.resetDados();
      } finally {
        this.carregando = false;
      }
    },

    resetDados() {
      this.resumo = {};
      this.periodo = {};
      this.cache = {};
      this.produtos = [];
      this.dadosFiliais = [];
      this.dadosCategorias = [];
      this.dadosFornecedores = [];
    }
  }
};
</script>

<style scoped>
.rupturas-dashboard {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.loading-text {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  padding: 40px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.no-data-message h3 {
  margin: 0;
  color: #023449;
  font-size: 1.25rem;
  font-weight: 700;
}

.no-data-message p {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
  text-align: center;
}

@media (max-width: 768px) {
  .loading-state,
  .no-data-message {
    min-height: 300px;
    padding: 24px 16px;
  }
}
</style>